import { useState, useEffect, useCallback, useMemo } from 'react'
import { cn } from '@/lib/utils'
import { CountrySelector } from './CountrySelector'
import { Country, findCountryByPhoneCode } from '@/data/countries'

interface PhoneInputProps {
  value?: string
  onChange: (phoneNumber: string, country: Country) => void
  placeholder?: string
  className?: string
  disabled?: boolean
  error?: string
  required?: boolean
}

export function PhoneInput({
  value = '',
  onChange,
  placeholder = "Número de telefone",
  className,
  disabled = false,
  error,
  required = false
}: PhoneInputProps) {
  const [selectedCountry, setSelectedCountry] = useState<Country | undefined>()
  const [phoneNumber, setPhoneNumber] = useState('')

  // Initialize with São Tomé e Príncipe as default
  useEffect(() => {
    if (!selectedCountry) {
      const stp = findCountryByPhoneCode('+239')
      if (stp) {
        setSelectedCountry(stp)
        // Don't call onChange immediately to avoid triggering form validation
        // onChange will be called when user actually interacts with the input
      }
    }
  }, []) // Remove onChange dependency to prevent infinite loops

  // Parse initial value if provided
  useEffect(() => {
    if (value && !phoneNumber) {
      // Try to extract country code and number from the full phone number
      const country = findCountryByPhoneCode(value.substring(0, 4)) // Try first 4 chars
      if (country) {
        setSelectedCountry(country)
        setPhoneNumber(value.substring(country.phoneCode.length))
      } else {
        // If no country found, assume it's just the number part
        setPhoneNumber(value)
      }
    }
  }, [value]) // Remove phoneNumber from dependencies to prevent infinite loop

  const handleCountryChange = useCallback((country: Country) => {
    setSelectedCountry(country)
    // Always call onChange when country changes, even if phoneNumber is empty
    const fullPhoneNumber = country.phoneCode + phoneNumber
    onChange(fullPhoneNumber, country)
  }, [phoneNumber, onChange])

  const handleNumberChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value
    // Only allow digits and spaces
    const cleanValue = inputValue.replace(/[^\d\s]/g, '')
    setPhoneNumber(cleanValue)
    
    // Only call onChange if we have a country selected
    // This prevents triggering nationality changes on every digit typed
    if (selectedCountry) {
      const fullPhoneNumber = selectedCountry.phoneCode + cleanValue
      onChange(fullPhoneNumber, selectedCountry)
    }
  }, [selectedCountry, onChange])

  const formatPhoneNumber = useCallback((number: string) => {
    // Remove all non-digits
    const digits = number.replace(/\D/g, '')
    
    // Format based on length
    if (digits.length <= 3) {
      return digits
    } else if (digits.length <= 6) {
      return `${digits.slice(0, 3)} ${digits.slice(3)}`
    } else if (digits.length <= 9) {
      return `${digits.slice(0, 3)} ${digits.slice(3, 6)} ${digits.slice(6)}`
    } else {
      return `${digits.slice(0, 3)} ${digits.slice(3, 6)} ${digits.slice(6, 9)}`
    }
  }, [])

  return (
    <div className={cn("space-y-2", className)}>
      <div className="flex space-x-2">
        {/* Country Selector */}
        <div className="w-48">
          <CountrySelector
            value={selectedCountry}
            onChange={handleCountryChange}
            placeholder="País"
            disabled={disabled}
            error={error}
          />
        </div>

        {/* Phone Number Input */}
        <div className="flex-1">
          <input
            type="tel"
            value={formatPhoneNumber(phoneNumber)}
            onChange={handleNumberChange}
            placeholder={placeholder}
            disabled={disabled}
            required={required}
            className={cn(
              "w-full px-3 py-2 border rounded-lg transition-colors",
              "focus:outline-none focus:ring-2 focus:ring-primary focus:ring-opacity-20",
              error 
                ? "border-error focus:border-error focus:ring-error/20" 
                : "border-gray-300 focus:border-primary",
              disabled 
                ? "bg-gray-50 text-gray-400 cursor-not-allowed" 
                : "bg-white text-gray-900"
            )}
          />
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <p className="text-sm text-error">{error}</p>
      )}

      {/* Helper Text */}
      {selectedCountry && (
        <p className="text-xs text-gray-500">
          Formato: {selectedCountry.phoneCode} XXX XXX XXX
        </p>
      )}
    </div>
  )
}
